import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Link as MuiLink,
    Divider
} from '@mui/material';
import { useNotify } from 'react-admin';
import { QRCodeSVG } from 'qrcode.react';
import { createImpersonationWebLink } from '../utils/impersonationHelpers';
import logger from '../../../utils/logger';
const ImpersonationQRDialog = ({ 
    open, 
    onClose, 
    deepLink,
    patient 
}) => {
    const notify = useNotify();

    // Debug logging
    logger.log('🔍 ImpersonationQRDialog received deepLink:', deepLink);
    logger.log('🔍 ImpersonationQRDialog deepLink type:', typeof deepLink);
    logger.log('🔍 ImpersonationQRDialog deepLink length:', deepLink?.length);

    // Don't render if no deepLink or if dialog is not open
    if (!open || !deepLink || deepLink.trim() === '') {
        return null;
    }

    const webLink = createImpersonationWebLink(deepLink);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(deepLink);
            notify('Deep link copied to clipboard', { type: 'info' });
        } catch (error) {
            // Fallback for older browsers or when clipboard API fails
            const textArea = document.createElement('textarea');
            textArea.value = deepLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            notify('Deep link copied to clipboard', { type: 'info' });
        }
    };

    const handleCopyWebLink = async () => {
        if (!webLink) return;
        
        try {
            await navigator.clipboard.writeText(webLink);
            notify('Web link copied to clipboard', { type: 'info' });
        } catch (error) {
            // Fallback for older browsers or when clipboard API fails
            const textArea = document.createElement('textarea');
            textArea.value = webLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            notify('Web link copied to clipboard', { type: 'info' });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="impersonation-qr-dialog-title"
            aria-describedby="impersonation-qr-dialog-description"
            maxWidth="sm"
            fullWidth
            PaperProps={{
                role: 'dialog',
                'aria-modal': true
            }}
        >
            <DialogTitle id="impersonation-qr-dialog-title">
                Impersonation Session Created
            </DialogTitle>
            <DialogContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography 
                        variant="h6" 
                        gutterBottom
                        id="impersonation-qr-dialog-description"
                    >
                        Impersonate {patient?.first_name} {patient?.last_name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Use your mobile device to scan the QR code, or click one of the links below for testing
                    </Typography>

                    {/* QR Code */}
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                    }}>
                                            <QRCodeSVG 
                        value={deepLink} 
                        size={200}
                        level="M"
                        includeMargin={true}
                        aria-label={`QR code for impersonating ${patient?.first_name} ${patient?.last_name}`}
                        role="img"
                    />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Deep Link */}
                    <Typography variant="subtitle2" gutterBottom>
                        Mobile App Link (Deep Link):
                    </Typography>
                    <Box sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        wordBreak: 'break-all'
                    }}>
                        <MuiLink
                            href={deepLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                                fontSize: '0.875rem',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            {deepLink}
                        </MuiLink>
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCopyLink}
                        sx={{ mt: 1 }}
                    >
                        Copy Deep Link
                    </Button>

                    {webLink && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                                Web Browser Link (For Testing):
                            </Typography>
                            <Box sx={{ 
                                p: 2, 
                                bgcolor: 'grey.50', 
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                wordBreak: 'break-all'
                            }}>
                                <MuiLink
                                    href={webLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        fontSize: '0.875rem',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {webLink}
                                </MuiLink>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCopyWebLink}
                                sx={{ mt: 1 }}
                            >
                                Copy Web Link
                            </Button>
                        </>
                    )}

                    <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                        This session will be logged for audit purposes
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    color="primary" 
                    variant="contained"
                    autoFocus
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImpersonationQRDialog; 